import { getAssetUrl } from './useAssetUrl'
import { createNadDebug } from '../utils/debugLog'
import { stripUrlQuery } from '../utils/stripUrlQuery'

const mediaFetchLog = createNadDebug('dw:timeline-playback:preload')

export interface MediaUrlOptions {
  width?: number
  cacheKey?: string
}

export interface MediaBlobFetchOptions {
  signal?: AbortSignal
}

/**
 * Build a browser media element URL. Authentication must be embedded in the
 * URL by getAssetUrl() because media elements and credentialless blob fetches
 * cannot rely on Authorization headers.
 */
export function getMediaElementUrl(
  projectId: string,
  assetPath: string | null | undefined,
  options: MediaUrlOptions = {},
): string | null {
  return getAssetUrl(projectId, assetPath, options.width ?? 0, options.cacheKey)
}

export async function fetchMediaBlob(
  projectId: string,
  assetPath: string | null | undefined,
  options: MediaBlobFetchOptions = {},
): Promise<Response> {
  const url = getMediaElementUrl(projectId, assetPath)
  if (!url) {
    throw new Error('Cannot fetch media blob without an asset URL')
  }
  return fetchResolvedMediaBlob(url, options)
}

export async function fetchResolvedMediaBlob(
  url: string,
  options: MediaBlobFetchOptions = {},
): Promise<Response> {
  const fetchUrl = withApiAssetProxy(url)

  mediaFetchLog({
    event: 'media-blob-fetch',
    urlKind: isApiAssetUrl(fetchUrl) ? 'api-asset' : 'absolute',
    hasEmbeddedAuth: hasEmbeddedAuth(fetchUrl),
    credentialsPolicy: 'omit',
    sendsAuthorization: false,
    sendsContentType: false,
  })

  try {
    const response = await fetch(fetchUrl, {
      signal: options.signal,
      credentials: 'omit',
    })

    if ((response.status === 401 || response.status === 403) && isApiAssetUrl(fetchUrl) && !hasEmbeddedAuth(fetchUrl)) {
      throw new Error(
        `Media asset URL is missing embedded auth for credentialless fetch: ${stripUrlQuery(fetchUrl)}`,
      )
    }

    return response
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Credentialless media blob fetch failed for ${stripUrlQuery(fetchUrl)}: ${error.message}`)
    }
    throw error
  }
}

function withApiAssetProxy(url: string): string {
  if (!isApiAssetUrl(url)) {
    return url
  }
  try {
    const parsed = new URL(url, getUrlParseBase())
    parsed.searchParams.set('proxy', 'true')
    return parsed.toString()
  } catch {
    return url
  }
}

function isApiAssetUrl(url: string): boolean {
  try {
    const parsed = new URL(url, getUrlParseBase())
    return /^\/projects\/[^/]+\/assets\//.test(parsed.pathname)
  } catch {
    return false
  }
}

function hasEmbeddedAuth(url: string): boolean {
  try {
    const parsed = new URL(url, getUrlParseBase())
    return parsed.searchParams.has('token') || parsed.searchParams.has('publication_token')
  } catch {
    return false
  }
}

function getUrlParseBase(): string {
  return typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
}
